/**
 * Color-coded badge for classification levels
 */

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { ClassificationLevel } from '@/types/discovery'

interface ClassificationBadgeProps {
  readonly level: ClassificationLevel
  readonly className?: string
}

const levelConfig: Record<
  ClassificationLevel,
  { label: string; className: string }
> = {
  PUBLIC: {
    label: 'Public',
    className: 'border-muted-foreground/30 text-muted-foreground',
  },
  INTERNAL: {
    label: 'Internal',
    className: 'border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400',
  },
  CONFIDENTIAL: {
    label: 'Confidential',
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
  RESTRICTED: {
    label: 'Restricted',
    className: 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400',
  },
  TOP_SECRET: {
    label: 'Top Secret',
    className:
      'border-purple-500/30 bg-purple-500/10 text-purple-700 dark:text-purple-400',
  },
}

export function ClassificationBadge({
  level,
  className,
}: ClassificationBadgeProps) {
  const config = levelConfig[level as keyof typeof levelConfig] ?? {
    label: level?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) ?? 'Unknown',
    className: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  }

  return (
    <Badge variant="outline" className={cn(config.className, className)}>
      {config.label}
    </Badge>
  )
}
